import { FC, ReactNode } from 'react'
import {
  AlertCircleIcon,
  CheckCircleIcon,
  CircleXIcon,
  cn,
  InfoIcon,
} from 'oddle-shop-monorepo.design-system'
import { useTranslate } from '@tolgee/react'
import { P } from '@/components/common/Typo'

export const notificationStates = {
  success: 'success',
  informative: 'informative',
  warning: 'warning',
  error: 'error',
} as const

type NotificationState =
  (typeof notificationStates)[keyof typeof notificationStates]

interface InlineNotificationProps {
  className?: string
  state?: NotificationState
  lightTheme?: boolean
  children?: ReactNode
  text?: string
  subText?: ReactNode
  withIcon?: boolean
  textClassName?: string
  subTextClassName?: string
}

export const getStateStyles = (
  state: NotificationState,
  lightTheme: boolean
) => {
  const baseStyles = 'rounded-lg p-3'
  const iconStyles = 'h-6 w-6'

  const styles = {
    success: {
      container: lightTheme
        ? 'bg-[#DDF7E6] text-[#067E30] [&>*]:text-[#067E30]'
        : 'bg-[#067E30] text-[#DDF7E6] [&>*]:text-[#DDF7E6]',
      icon: lightTheme ? 'text-[#067E30]' : 'text-[#DDF7E6]',
    },
    informative: {
      container: lightTheme
        ? 'bg-[#D7E9FF] text-[#0C62CA] [&>*]:text-[#0C62CA]'
        : 'bg-[#0C62CA] text-[#D7E9FF] [&>*]:text-[#D7E9FF]',
      icon: lightTheme ? 'text-[#0C62CA]' : 'text-[#D7E9FF]',
    },
    warning: {
      container: lightTheme
        ? 'bg-[#FFF1BB] text-black [&>*]:text-black'
        : 'bg-[#FFCF35] text-black [&>*]:text-black',
      icon: 'text-black',
    },
    error: {
      container: lightTheme
        ? 'bg-[#FFEEEB] text-[#D62105] [&>*]:text-[#D62105]'
        : 'bg-[#D62105] text-[#FFEEEB] [&>*]:text-[#FFEEEB]',
      icon: lightTheme ? 'text-[#D62105]' : 'text-[#FFEEEB]',
    },
  }

  return {
    container: `${baseStyles} ${styles[state].container}`,
    icon: `${iconStyles} ${styles[state].icon}`,
  }
}

const getIcon = (state: NotificationState) => {
  switch (state) {
    case notificationStates.success:
      return CheckCircleIcon
    case notificationStates.informative:
      return InfoIcon
    case notificationStates.warning:
      return AlertCircleIcon
    case notificationStates.error:
      return CircleXIcon
  }
}

export const InlineNotification: FC<InlineNotificationProps> = ({
  className = '',
  state = notificationStates.informative,
  lightTheme = true,
  children,
  text,
  subText,
  withIcon = true,
  textClassName,
  subTextClassName,
}) => {
  const { t } = useTranslate()
  const styles = getStateStyles(state, lightTheme)
  const Icon = getIcon(state)

  return (
    <div className={cn(styles.container, className)}>
      <div className="flex items-start">
        {withIcon && (
          <div className="mr-3 flex-shrink-0 self-center">
            <Icon className={styles.icon} aria-hidden="true" />
          </div>
        )}
        <div className="flex-1">
          {children || (
            <>
              {text && (
                <P
                  className={cn(
                    styles.container,
                    'mb-0 font-bold',
                    textClassName
                  )}
                >
                  {t(text, text)}
                </P>
              )}
              {subText && (
                <div className={cn(styles.container, 'mt-1', subTextClassName)}>
                  {typeof subText === 'string' ? t(subText) : subText}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
